import { Inject, Injectable, OnDestroy, Optional, PLATFORM_ID } from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { CookieService } from '~shared/services/cookie.service';
import { BehaviorSubject, Subject } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { PriceTypes } from '../models/price-types.enum';
import { PRICE_TYPE_COOKIE_NAME, PRICE_TYPE_EXPLANATION_COOKIE_NAME } from '../constants/cookie-name.constant';
import { getUserData, UserInfoState } from '~shared/modules/user-info/store';
import { AccountStatusEnum, USER_ROLES } from '~shared/constants/account-info.constant';
import { IAccountInformation } from '~shared/models';
import { takeUntil } from 'rxjs/operators';
import { BusinessTypeEnum } from '~shared/constants/business-model.enum';
import {
  MmPopoverConfig,
  MmPopoverRef,
  MmPopoverService,
  PreferablePositions,
} from '~shared/modules/popover/popover.module';
import { PriceTypeExplanationPopupComponent } from '../components/price-type-explanation-popup/price-type-explanation-popup.component';
import { MediaObserver } from '@angular/flex-layout';
import { MEDIA_ALISES } from '~shared/constants/media.constant';
import { NUMBER_1, NUMBER_500 } from '~shared/constants';
import { createCookieExpiration } from '~shared/modules/price-type-toggler/utils/price-type-cookies.util';
import { StoryblokService } from '~app/modules/storyblok/services';
import { ReadShoppingCart } from '~shared/modules/shopping-cart-store/store/shopping-cart.actions';
import { REQUEST } from '@nguniversal/express-engine/tokens';
import { MarketingFeedQueryParams, MFeedBusinessModel } from '~app/modules/product/constants/marketing-feed.constants';

@Injectable({
  providedIn: 'root',
})
export class PriceTypeService implements OnDestroy {
  public priceType$ = new BehaviorSubject<PriceTypes>(PriceTypes.b2b);
  public isTogglerActive$ = new BehaviorSubject<boolean>(true);
  private destroySubject$ = new Subject();
  private priceTypeExplanationRef: MmPopoverRef;

  constructor(
    private cookieService: CookieService,
    private store: Store<UserInfoState>,
    @Inject(PLATFORM_ID) private platformId: Object,
    @Inject(DOCUMENT) private document: Document,
    private mediaObserver: MediaObserver,
    private popoverSvc: MmPopoverService,
    private storyblokService: StoryblokService,
    @Optional() @Inject(REQUEST) private webAppBuyerRequest: any
  ) {
    const mfeed_bm = this.getMfeedBusinessModel();
    if (isPlatformBrowser(this.platformId)) {
      this.store
        .pipe(select(getUserData), takeUntil(this.destroySubject$))
        .subscribe((accountInformation: IAccountInformation) => {
          // This is being called in setTimeout because in the very initial stage
          // there is a chance that all StoreEffects are not yet registered.
          // This gives the StoreEffects the time window needed to be registered
          setTimeout(() => {
            if (!!mfeed_bm && (!accountInformation || accountInformation.role === USER_ROLES.guest)) {
              this.setPriceType(mfeed_bm);
            } else {
              this.updatePriceType(accountInformation);
            }
          });
        });
    } else {
      if (!!mfeed_bm) {
        this.setPriceType(mfeed_bm);
      } else {
        this.initializePriceType();
      }
    }
  }

  ngOnDestroy(): void {
    this.destroySubject$.next();
    this.destroySubject$.complete();
    this.priceType$.complete();
    this.isTogglerActive$.complete();
  }

  getMfeedBusinessModel() {
    let mfeed_bm;
    if (isPlatformBrowser(this.platformId)) {
      const params = new URLSearchParams(window.location.search);
      mfeed_bm = Number(params.get(MarketingFeedQueryParams.BusinessModel));
    } else {
      mfeed_bm =
        !!this.webAppBuyerRequest && Number(this.webAppBuyerRequest?.query[MarketingFeedQueryParams.BusinessModel]);
    }
    switch (mfeed_bm) {
      case MFeedBusinessModel.B2C:
        return PriceTypes.b2c;
      case MFeedBusinessModel.B2B:
        return PriceTypes.b2b;
      default:
        return null;
    }
  }

  private isValidPriceType(priceType: PriceTypes): boolean {
    return Object.values(PriceTypes).some((value) => value === priceType);
  }

  private getCookiePriceType(): PriceTypes {
    let cookiePriceType;
    if (isPlatformBrowser(this.platformId)) {
      cookiePriceType = this.cookieService.get(PRICE_TYPE_COOKIE_NAME);
    } else {
      const reqCookie = this.webAppBuyerRequest?.headers?.cookie;
      const parsedCookies = !reqCookie
        ? []
        : reqCookie.split(';')?.map((parsedCookie) => parsedCookie.trim().split('='));
      const cookie = parsedCookies?.find(([key, _]) => key === 'priceType');
      cookiePriceType = cookie && cookie[NUMBER_1];
    }
    switch (cookiePriceType) {
      case PriceTypes.b2c:
        return PriceTypes.b2c;
      case PriceTypes.b2b:
        return PriceTypes.b2b;
      default:
        return PriceTypes.b2b;
    }
  }

  public setPriceType(priceType: PriceTypes): void {
    const isValidType = this.isValidPriceType(priceType);
    if (!isValidType) {
      throw Error('Price type has invalid value');
    }
    const expires = createCookieExpiration();
    this.cookieService.set(PRICE_TYPE_COOKIE_NAME, priceType, expires, '/');
    this.priceType$.next(priceType);
    if (isPlatformBrowser(this.platformId) && window?.location && !window?.location?.href?.includes('/checkout/checkout/order/review')) {
      this.store.dispatch(new ReadShoppingCart());
    }
  }

  public getPriceTypeSync(): PriceTypes {
    return this.priceType$.getValue();
  }

  public getBusinessTypeSync(): BusinessTypeEnum {
    const priceType: PriceTypes = this.getPriceTypeSync();
    return priceType === PriceTypes.b2b ? BusinessTypeEnum.B2B : BusinessTypeEnum.B2C;
  }

  private updatePriceType(accountInfo: IAccountInformation): void {
    const { role, status } = (accountInfo || {}) as IAccountInformation;
    const isConsumer = role === USER_ROLES.consumer;
    const isEntrepreneur = role === USER_ROLES.entrepreneur;
    const isAccountWaitForValidation = status === AccountStatusEnum.validation;

    switch (true) {
      case isConsumer:
      case isEntrepreneur && isAccountWaitForValidation:
        this.setPriceType(PriceTypes.b2c);
        this.isTogglerActive$.next(false);
        break;

      case isEntrepreneur:
        this.setPriceType(PriceTypes.b2b);
        this.isTogglerActive$.next(false);
        break;

      default:
        this.initializePriceType();
        break;
    }
  }

  private initializePriceType(): void {
    const cookiePriceType = this.getCookiePriceType();
    const initialPriceType = this.isValidPriceType(cookiePriceType) ? cookiePriceType : PriceTypes.b2b;
    if (isPlatformBrowser(this.platformId)) {
      const isDesktop = this.mediaObserver.isActive(MEDIA_ALISES.desktop);
      const isPriceTypeExplanationCookieExist = !!this.cookieService.get(PRICE_TYPE_EXPLANATION_COOKIE_NAME);

      if (isDesktop && !isPriceTypeExplanationCookieExist && !this.storyblokService.isPreviewMode()) {
        this.showPriceTypeExplanation();
      }
    }

    this.setPriceType(initialPriceType);
    this.isTogglerActive$.next(true);
  }

  private showPriceTypeExplanation() {
    if (this.priceTypeExplanationRef) {
      this.priceTypeExplanationRef.close();
    }

    setTimeout(() => {
      const config = this.getPriceExplanationConfig();
      this.priceTypeExplanationRef = this.popoverSvc.open({
        ...config,
      });
    }, NUMBER_500);
  }

  private getPriceExplanationConfig(): MmPopoverConfig<{}> {
    const originElem = this.document.getElementsByClassName('label-price-switch')[0] as HTMLElement
    return {
      content: PriceTypeExplanationPopupComponent,
      data: {},
      hasBackdrop: false,
      origin: originElem,
      preferedPosition: PreferablePositions.bottomRight,
    };
  }
}
